"use client"

import { HttpTypes } from "@medusajs/types"
import { useCallback, useEffect, useState } from "react"

import X from "@modules/common/icons/x"
import T from "@lib/i18n/t"
import { useT } from "@lib/i18n/client"
import ShirtPreview from "@modules/products/components/shirt-preview"
import { useShirtColor } from "@modules/products/components/shirt-color"

type ImageGalleryProps = {
  images: HttpTypes.StoreProductImage[]
  /** "mono" = nadruk jednobarwny, na ciemnych koszulkach drukowany bielą. */
  printMode?: "mono" | "color"
}

/**
 * Grafiki produktów to projekty nadruków: mają przezroczyste tło i ciemny rysunek,
 * a ich proporcje są skrajnie różne (od 0.67 do niemal 6.0). Dlatego wszędzie
 * używamy object-contain na jasnym podłożu — object-cover ucinał je nie do poznania,
 * a ciemne tło zjadało czarne kreski.
 *
 * Kafelek pokazuje nadruk na makiecie koszulki w kolorze wybranym obok
 * (wszystkie nasze nadruki są z przodu), a kliknięcie otwiera samą grafikę
 * w pełnej rozdzielczości — makieta pokazuje proporcje, lightbox szczegóły.
 */
const ImageGallery = ({ images, printMode = "color" }: ImageGalleryProps) => {
  const t = useT()
  const { color } = useShirtColor()
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const close = useCallback(() => setLightboxIndex(null), [])

  const step = useCallback(
    (delta: number) =>
      setLightboxIndex((current) =>
        current === null ? null : (current + delta + images.length) % images.length
      ),
    [images.length]
  )

  // Klawiatura + blokada przewijania tła, gdy lightbox jest otwarty.
  useEffect(() => {
    if (lightboxIndex === null) {
      return
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
      if (e.key === "ArrowRight") step(1)
      if (e.key === "ArrowLeft") step(-1)
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    window.addEventListener("keydown", onKey)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener("keydown", onKey)
    }
  }, [lightboxIndex, close, step])

  const active = lightboxIndex === null ? null : images[lightboxIndex]

  return (
    <>
      <div className="flex items-start relative">
        <div className="flex flex-col flex-1 small:mx-16 gap-y-4">
          {images.map((image, index) => (
            <button
              key={image.id}
              type="button"
              onClick={() => setLightboxIndex(index)}
              aria-label={`Pokaż grafikę ${index + 1} z ${images.length} w pełnym rozmiarze`}
              className="ateisci-art-surface group relative flex aspect-[4/5] w-full items-center justify-center overflow-hidden rounded-large border border-ui-border-base p-4 shadow-elevation-card-rest transition-shadow duration-200 hover:shadow-elevation-card-hover cursor-zoom-in"
              id={image.id}
              data-testid="product-image"
            >
              {/* Szerokość, nie wysokość: makieta jest smuklejsza (400/470) niż
                  kafelek (4/5), więc rozciągnięta na wysokość wychodziła
                  rękawami poza kadr. */}
              <ShirtPreview
                className="w-full transition-transform duration-300 group-hover:scale-[1.02]"
                printUrl={image.url}
                color={color}
                printMode={printMode}
                alt={`Grafika produktu ${index + 1} na koszulce`}
              />
              <span className="pointer-events-none absolute bottom-3 right-3 rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white opacity-0 backdrop-blur-sm transition-opacity duration-200 group-hover:opacity-100">
                <T>Kliknij, aby zobaczyć samą grafikę</T>
              </span>
            </button>
          ))}
        </div>
      </div>

      {active && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("Podgląd grafiki w pełnym rozmiarze")}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm small:p-8"
          onClick={close}
          data-testid="image-lightbox"
        >
          <button
            type="button"
            onClick={close}
            aria-label={t("Zamknij podgląd")}
            className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <X size={22} />
          </button>

          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  step(-1)
                }}
                aria-label={t("Poprzednia grafika")}
                className="absolute left-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-2xl leading-none text-white transition-colors hover:bg-white/20"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  step(1)
                }}
                aria-label={t("Następna grafika")}
                className="absolute right-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-2xl leading-none text-white transition-colors hover:bg-white/20"
              >
                ›
              </button>
              <span className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-sm text-white">
                {(lightboxIndex ?? 0) + 1} / {images.length}
              </span>
            </>
          )}

          {/* Jasne podłoże także w lightboksie — bez niego czarny nadruk znika. */}
          <div
            className="ateisci-art-surface max-h-[92vh] max-w-[95vw] overflow-auto rounded-large p-4 shadow-2xl small:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Celowo natywny <img> z oryginalnym plikiem: lightbox ma pokazać
                grafikę w pełnej rozdzielczości, bez skalowania przez next/image. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={active.url}
              alt="Grafika produktu w pełnym rozmiarze"
              className="max-h-[84vh] w-auto max-w-full object-contain"
            />
          </div>
        </div>
      )}
    </>
  )
}

export default ImageGallery
